/**
 * Acumulador do medidor de dano. Sem React, sem DOM: a janela só desenha o
 * retrato de 1 s. Fechar descarta a instância; zerar recomeça nela.
 */

export const STRIKE_CAP = 16;
export const BALDE_MS = 5_000;
export const BALDES = 24;

export type GolpeMedidor = {
  readonly dealt: number;
  readonly miss: boolean;
  readonly kill: boolean;
  readonly crit: boolean;
  readonly kind: string;
};

export type AlvoDps = {
  readonly nome: string;
  readonly dano: number;
  readonly lutaS: number;
  readonly dps: number | null;
};

export type BaldeDps = {
  readonly dps: number;
  readonly vazio: boolean;
};

export type DpsSnapshot = {
  readonly dano: number;
  readonly criticos: number;
  readonly maior: number;
  readonly mortes: number;
  readonly sofrido: number;
  readonly paredeS: number;
  readonly lutaS: number;
  readonly dpsLuta: number | null;
  readonly dpsCorrido: number | null;
  readonly dpsSofrido: number | null;
  readonly alvos: readonly AlvoDps[];
  readonly baldes: readonly BaldeDps[];
  readonly foraDeLuta: number;
  readonly quadrosNoTeto: number;
  readonly vazio: boolean;
};

export function meuGolpe(atacante: number, youId: number): boolean {
  if (atacante === 0) return false;
  return atacante === youId;
}

export function atacanteDoBruto(raw: unknown): number {
  if (raw == null) return 0;
  if (Array.isArray(raw)) return Number(raw[6]) || 0;
  const rec = raw as Record<string, unknown>;
  const v = rec[6] ?? rec['6'] ?? rec.Atacante ?? rec.atacante;
  return Number(v) || 0;
}

function contaSaida(g: GolpeMedidor, meu: boolean): boolean {
  return meu && !g.miss && g.kind !== 'cura';
}

function contaSofrido(g: GolpeMedidor, emVoce: boolean): boolean {
  return emVoce && !g.miss && g.kind !== 'cura';
}

type AlvoAcc = {
  dano: number;
  segundos: Set<number>;
};

function dpsDe(dano: number, segundos: number): number | null {
  if (segundos <= 0 || dano <= 0) return null;
  return dano / segundos;
}

export class MedidorDano {
  private abertoEm = 0;
  private dano = 0;
  private criticos = 0;
  private maior = 0;
  private mortes = 0;
  private sofrido = 0;
  private quadrosNoTeto = 0;
  private readonly luta = new Set<number>();
  private readonly alvos = new Map<string, AlvoAcc>();
  private readonly baldeDano = new Map<number, number>();
  private readonly baldeGolpe = new Set<number>();

  constructor(private readonly agora: () => number = Date.now) {
    this.abertoEm = this.agora();
  }

  zerar() {
    this.abertoEm = this.agora();
    this.dano = 0;
    this.criticos = 0;
    this.maior = 0;
    this.mortes = 0;
    this.sofrido = 0;
    this.quadrosNoTeto = 0;
    this.luta.clear();
    this.alvos.clear();
    this.baldeDano.clear();
    this.baldeGolpe.clear();
  }

  marcarTeto() {
    this.quadrosNoTeto += 1;
  }

  /** Soma o `dealt` do golpe — overkill incluso, sem cortar na vida do alvo. */
  aplicar(g: GolpeMedidor, meu: boolean, emVoce: boolean, nome: string) {
    const saida = contaSaida(g, meu);
    const toma = contaSofrido(g, emVoce);
    if (!saida && !toma) return;

    const t = this.agora();
    const segundo = Math.floor(t / 1000);
    const balde = Math.floor(t / BALDE_MS);
    this.luta.add(segundo);
    this.baldeGolpe.add(balde);

    if (toma) this.sofrido += g.dealt;
    if (!saida) return;

    this.dano += g.dealt;
    if (g.crit) this.criticos += 1;
    if (g.dealt > this.maior) this.maior = g.dealt;
    if (g.kill) this.mortes += 1;
    this.baldeDano.set(balde, (this.baldeDano.get(balde) ?? 0) + g.dealt);

    const rotulo = nome;
    let alvo = this.alvos.get(rotulo);
    if (!alvo) {
      alvo = { dano: 0, segundos: new Set() };
      this.alvos.set(rotulo, alvo);
    }
    alvo.dano += g.dealt;
    alvo.segundos.add(segundo);
  }

  snapshot(): DpsSnapshot {
    const paredeS = Math.max(0, (this.agora() - this.abertoEm) / 1000);
    const lutaS = this.luta.size;
    const alvos = [...this.alvos.entries()]
      .map(([nome, a]) => {
        const s = a.segundos.size;
        return { nome, dano: a.dano, lutaS: s, dps: dpsDe(a.dano, s) };
      })
      .sort((a, b) => b.dano - a.dano);

    const current = Math.floor(this.agora() / BALDE_MS);
    const baldes: BaldeDps[] = [];
    let foraDeLuta = 0;
    for (let i = BALDES - 1; i >= 0; i--) {
      const id = current - (BALDES - 1) + i;
      const vazio = !this.baldeGolpe.has(id);
      if (vazio) foraDeLuta += 1;
      baldes.push({ dps: (this.baldeDano.get(id) ?? 0) / (BALDE_MS / 1000), vazio });
    }

    return {
      dano: this.dano,
      criticos: this.criticos,
      maior: this.maior,
      mortes: this.mortes,
      sofrido: this.sofrido,
      paredeS,
      lutaS,
      dpsLuta: dpsDe(this.dano, lutaS),
      dpsCorrido: dpsDe(this.dano, paredeS),
      dpsSofrido: dpsDe(this.sofrido, lutaS),
      alvos,
      baldes,
      foraDeLuta,
      quadrosNoTeto: this.quadrosNoTeto,
      vazio: this.dano === 0 && this.criticos === 0 && this.mortes === 0 && this.maior === 0
    };
  }
}
